import React from "react";
import { useTranslation } from "react-i18next";
import { useEditAllocation, useEditTask } from "@planzone/view-models";
import { allocation, task as taskMock } from "@planzone/dto";
import { useModal } from "@root/client/modal";
import { TaskCompletionStatus } from "@common/taskCompletionStatus";
import { ScrollingText } from "@common/scrollingText";
import { Safeguard } from "@scenes/safeguard";
import { AutoFocusContext } from "../../../autoFocusCtx";
import { RecordList } from "../../recordList";
import { isLastIncompleteAllocation } from "../utils";
import { Props } from "./types";
import styles from "./styles.scss";
import { UseCases } from "@planzone/core";

function Task({
  project,
  task,
  resource,
  depth = 1,
  areDetailsDisplayed,
  isReloading,
  showCompletionColumn,
  timesheetDeadline,
  isResourceInProject,
  resourceRemoved,
}: Props) {
  const { t } = useTranslation();
  const { pushView } = useModal();
  const { editTask } = useEditTask({ ...taskMock, ...task });
  const { editAllocation } = useEditAllocation({ ...allocation, uuid: "" });

  const { lastAllocatedTaskUuid, clearLastAllocated } =
    React.useContext(AutoFocusContext);
  const isHighlighted = lastAllocatedTaskUuid === task.uuid;
  const rowRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (isHighlighted) {
      rowRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      const timer = window.setTimeout(clearLastAllocated, 4000);
      return () => window.clearTimeout(timer);
    }
  }, [isHighlighted]);

  const isDone = task.percentComplete === 100;
  const userAllocation = task?.allocationData?.find(
    (alloc) => alloc.resourceUuid === resource.uuid,
  );

  return (
    <div className={styles.list}>
      <div
        ref={rowRef}
        className={`${styles.main} ${isHighlighted ? styles.highlighted : ""}`}
      >
        <div
          className={styles.header}
          style={{ paddingLeft: `${depth === 0 ? 4 : depth * 3 + 4}rem` }}
        >
          <Safeguard
            useCases={[
              UseCases.editTask,
              UseCases.markTaskAsCompleted,
              UseCases.editAllocation,
            ]}
          >
            {({ canMarkTaskAsCompleted, canEditTask, canEditAllocation }) => (
              <>
                <div
                  onClick={
                    !resourceRemoved &&
                    (isResourceInProject ||
                      canMarkTaskAsCompleted ||
                      canEditTask ||
                      canEditAllocation)
                      ? (e) => {
                          e.stopPropagation();
                          userAllocation
                            ? !isDone &&
                              (task.allocationData.length === 1 ||
                                isLastIncompleteAllocation(
                                  task.allocationData,
                                  userAllocation,
                                ))
                              ? pushView({
                                  key: UseCases.markTaskAsCompleted,
                                  data: {
                                    task: { ...taskMock, ...task },
                                    project,
                                  },
                                })
                              : editAllocation({
                                  allocation: {
                                    ...allocation,
                                    ...userAllocation,
                                    resource,
                                  },
                                  nextAllocation: {
                                    ...allocation,
                                    ...userAllocation,
                                    resource,
                                    percentComplete:
                                      task.resourcePercentComplete === 100
                                        ? 0
                                        : 100,
                                    allocatedHours:
                                      userAllocation.allocatedHours,
                                  },
                                })
                            : isDone
                              ? editTask({
                                  task: { ...taskMock, ...task },
                                  nextTask: {
                                    ...taskMock,
                                    ...task,
                                    percentComplete: 0,
                                    activityKeyRank: task.keyRank,
                                  },
                                })
                              : pushView({
                                  key: UseCases.markTaskAsCompleted,
                                  data: {
                                    task: { ...taskMock, ...task },
                                    project,
                                  },
                                });
                        }
                      : undefined
                  }
                  style={{
                    cursor: resourceRemoved
                      ? "default"
                      : isResourceInProject ||
                          canMarkTaskAsCompleted ||
                          canEditTask ||
                          canEditAllocation
                        ? "pointer"
                        : "not-allowed",
                  }}
                >
                  <TaskCompletionStatus
                    endDate={task.endDate}
                    progress={task.percentComplete}
                    resource={resource}
                    isDisabled={
                      (!isResourceInProject && !canEditTask) || resourceRemoved
                    }
                    task={{
                      ...taskMock,
                      ...task,
                      allocations: task.allocationData.map((alloc) => ({
                        ...allocation,
                        uuid: alloc.uuid,
                        allocatedHours: task.totalOfAllocatedHours,
                        percentComplete: alloc.percentComplete,
                        resource: {
                          ...resource,
                          uuid: alloc.resourceUuid,
                        },
                      })),
                    }}
                  />
                </div>
                <ScrollingText speed={100}>
                  <p
                    className={styles.name}
                    onClick={
                      !resourceRemoved &&
                      (isResourceInProject ||
                        canEditTask ||
                        canEditAllocation ||
                        canMarkTaskAsCompleted)
                        ? () =>
                            pushView({
                              key: UseCases.retrieveTask,
                              data: {
                                task: { ...taskMock, ...task },
                                isMultiProject: true,
                              },
                            })
                        : undefined
                    }
                    style={{
                      cursor: resourceRemoved
                        ? "default"
                        : isResourceInProject ||
                            canEditTask ||
                            canEditAllocation ||
                            canMarkTaskAsCompleted
                          ? "pointer"
                          : "not-allowed",
                    }}
                  >
                    {task.name}
                  </p>
                </ScrollingText>
                {resourceRemoved && (
                  <span className={styles.removedBadge}>
                    {t("timesheetPage.resourceRemoved")}
                  </span>
                )}
                {!task.resourceHaveAllocation && (
                  <span className={styles.noAllocationBadge}>
                    {t("timesheetPage.noAllocation")}
                  </span>
                )}
              </>
            )}
          </Safeguard>
        </div>
        <div className={styles.recordList}>
          <RecordList
            {...task}
            type="task"
            records={task.timeRecords}
            project={project}
            task={{ ...taskMock, ...task }}
            resource={resource}
            allocatedHours={task.totalOfAllocatedHours}
            spentTime={task.totalOfSpentTime}
            spentTimeInWeek={task.totalOfSpentTimeInWeek}
            remainingTime={task.totalOfRemainingTime}
            areDetailsDisplayed={areDetailsDisplayed}
            progression={task.resourcePercentComplete}
            isReloading={isReloading}
            showCompletionColumn={showCompletionColumn}
            timesheetDeadline={timesheetDeadline}
            resourceAllocation={userAllocation}
          />
        </div>
      </div>
    </div>
  );
}

export { Task };
